// src/components/Dashboard/CheckFiles.jsx
//
// Lista única de los archivos de un cheque: copia del cheque, enlace al CRM
// histórico y comprobantes de pago. La usan la tarjeta del cheque dentro del
// flujo (CheckCard, solo ver) y la pestaña "Cheques" (ClaimChecksTab, ver y
// descargar), que antes repetían este marcado por separado.
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { downloadBackup, openBackup } from '../../services/backupService';
import { money } from '../../documents/closingData';
import { crmLinkFor } from '../../utils/claimHelpers';
import { openCheckInvoice } from '../../utils/checkInvoice';
import { paidMessageFor } from '../../utils/paidMessage';

// Un fallo de red o de permisos llega como "HTTP 403" o "Failed to fetch": en una interfaz
// bilingüe se muestra un mensaje propio y el técnico solo va a la consola.
const fileError = (error, es) => {
  console.error('Check file action failed:', error);
  toast.error(es ? 'No se pudo abrir o descargar el archivo.' : 'Could not open or download the file.');
};

const FileRow = ({ icon, label, file, es, download }) => (
  <div className={`check-doc ${file ? 'is-done' : ''}`}>
    <span className="check-doc-icon"><i className={`fa-solid ${icon}`} aria-hidden="true"></i></span>
    <span className="check-doc-name">{label}{file?.name ? ` · ${file.name}` : ''}</span>
    {file && (
      <>
        <button type="button" className="cd-btn cd-btn-invoice" onClick={() => openBackup(file.storageKey).catch(e => fileError(e, es))}>
          <i className="fa-solid fa-eye" aria-hidden="true"></i> {es ? 'Ver' : 'View'}
        </button>
        {download && (
          <button type="button" className="cd-btn" onClick={() => downloadBackup(file.storageKey, file.name).catch(e => fileError(e, es))}>
            <i className="fa-solid fa-download" aria-hidden="true"></i> {es ? 'Descargar' : 'Download'}
          </button>
        )}
      </>
    )}
  </div>
);

const CheckFiles = ({ check, es, download = false }) => {
  const copy = check.checkAttachment?.storageKey ? check.checkAttachment : null;
  const crmLink = crmLinkFor(check);
  // Los comprobantes de pago son el cobro de CSR: solo Super Admin y P.A Admin los ven.
  const { can } = useAuth();
  const showMoney = can('viewEarnings');
  const receipts = showMoney ? (check.paymentHistory || []).filter(p => p.backup?.storageKey) : [];
  const canInvoice = can('downloadInvoice');
  // Número asignado en esta pantalla (la lista de cheques no se recarga sola).
  const [assignedInvoice, setAssignedInvoice] = useState('');
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const invoiceNumber = check.invoiceNumber || assignedInvoice;
  const openInvoice = async print => {
    setInvoiceBusy(true);
    try {
      setAssignedInvoice(await openCheckInvoice({ ...check, invoiceNumber }, { print }));
    } catch (error) {
      console.error('Invoice generation failed:', error);
      toast.error(es ? 'No se pudo generar el invoice.' : 'Could not generate the invoice.');
    } finally {
      setInvoiceBusy(false);
    }
  };
  const copyPaidMessage = () => navigator.clipboard.writeText(paidMessageFor(check, es))
    .then(() => toast.success(es ? 'Mensaje copiado' : 'Message copied'))
    .catch(() => toast.error(es ? 'No se pudo copiar' : 'Could not copy'));
  return (
    <div className="check-docs">
      <FileRow icon={copy ? 'fa-circle-check' : 'fa-file-circle-xmark'} es={es} file={copy} download={download}
        label={`${es ? 'Copia del cheque' : 'Check copy'}${copy ? '' : (es ? ' · sin archivo subido' : ' · no file uploaded')}`} />
      {receipts.map((payment, index) => (
        <FileRow key={index} icon="fa-receipt" es={es} file={payment.backup} download={download}
          label={`${es ? 'Comprobante de pago' : 'Payment receipt'}${payment.reference ? ` · ${payment.reference}` : ''}${payment.amount ? ` · ${money(payment.amount)}` : ''}`} />
      ))}
      {/* Comprobante de pago (BofA / Zelle): la fila se ve siempre, apagada mientras no haya uno subido. */}
      {showMoney && receipts.length === 0 && (
        <div className="check-doc">
          <span className="check-doc-icon"><i className="fa-solid fa-receipt" aria-hidden="true"></i></span>
          <span className="check-doc-name">{es ? 'Comprobante de pago (BofA / Zelle) · pendiente de backup' : 'Payment receipt (BofA / Zelle) · backup pending'}</span>
        </div>
      )}
      {/* Invoice: siempre a la mano. El número nace la primera vez que se genera,
          así se puede enviar al PA antes del pago. */}
      {canInvoice && (
        <div className={`check-doc ${invoiceNumber ? 'is-done' : ''}`}>
          <span className="check-doc-icon"><i className="fa-solid fa-file-invoice" aria-hidden="true"></i></span>
          <span className="check-doc-name">{invoiceNumber ? `Invoice #${invoiceNumber}` : (es ? 'Invoice · aún sin generar' : 'Invoice · not generated yet')}</span>
          <button type="button" className="cd-btn cd-btn-invoice" disabled={invoiceBusy} onClick={() => openInvoice(false)}>
            <i className={`fa-solid ${invoiceNumber ? 'fa-eye' : 'fa-file-invoice'}`} aria-hidden="true"></i> {invoiceNumber ? (es ? 'Ver' : 'View') : (es ? 'Generar' : 'Generate')}
          </button>
          <button type="button" className="cd-btn" disabled={invoiceBusy} onClick={() => openInvoice(true)}>
            <i className="fa-solid fa-print" aria-hidden="true"></i> {es ? 'Imprimir / PDF' : 'Print / PDF'}
          </button>
          {/* Cheque cobrado: el mensaje para el grupo del PA queda siempre a mano. */}
          {check.paid && (
            <button type="button" className="cd-btn" onClick={copyPaidMessage}>
              <i className="fa-solid fa-copy" aria-hidden="true"></i> {es ? 'Copiar mensaje de pagado' : 'Copy paid message'}
            </button>
          )}
        </div>
      )}
      {crmLink && (
        <div className="check-doc is-done">
          <span className="check-doc-icon"><i className="fa-solid fa-link" aria-hidden="true"></i></span>
          <span className="check-doc-name">{es ? 'Respaldo en el CRM histórico' : 'Backup in the historical CRM'}</span>
          <a className="cd-btn cd-btn-invoice" href={crmLink} target="_blank" rel="noopener noreferrer">
            <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i> {es ? 'Abrir' : 'Open'}
          </a>
        </div>
      )}
    </div>
  );
};

export default CheckFiles;
